import Breadcrumbs from '@components/molecules/products/breadcrumbs/breadcrumbs';
import clsx from 'clsx';
import DefaultLayout from '@layouts/default-layout';
import dynamic from 'next/dynamic';
import extractFromContext from '@helpers/utils/extract-from-context';
import Head, { generateAlternateLink } from '@components/atoms/head/head';
import logger from '@helpers/utils/logger/client';
import ProductDescription from '@components/molecules/products/product-description/product-description';
import ProductReport from '@components/molecules/products/product-report/product-report';
import ProductSchema from '@components/structured-schemas/product-schema';
import ProductService from '@services/product-service';
import React, { useEffect } from 'react';
import RequestError from '@errors/request-error';
import usePdpLayout from '@hooks/product/use-pdp-layout';
import useProductQuery from '@hooks/product/use-product-query';
import useRecentlyViewedProducts from '@hooks/product/use-recently-viewed-products';
import useUser from '@hooks/user/use-user';
import { Breadcrumb } from '@interfaces/models/breadcrumb';
import { ContentsLocalizedPath } from '@interfaces/models/contentsLocalizedPath';
import { dehydrate, DehydratedState, QueryClient, useHydrate } from '@tanstack/react-query';
import { extractUrlfromPrerenderDataReq } from '@helpers/routing';
import { getAnonymousUserWelcomeVoucher } from '@api/contents';
import { getBreadcrumbs } from '@api/breadcrumbs';
import { getOrCreateLDCookiesSsr } from '@components/bucketTest/bucket.utils';
import { GetServerSideProps, NextPageWithLayout } from 'next';
import { isRequest404Error, isRequestError } from '@helpers/errors';
import { mapProduct, mapProductPageUrl } from '@helpers/utils/analytics';
import { Product } from '@interfaces/models/product';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { Shipping } from '@interfaces/models/shipping';
import { SSRConfig } from 'next-i18next';
import { UpdateDataLayerComponent } from '@context/analytics.context';
import { useBucket } from '@context/bucket.context';
import { WelcomeVoucher } from '@interfaces/models/welcomeVoucher';
import {
  BreadcrumbsQueryKeys,
  GlobalQueryKeys,
  ProductPageQueryKeys,
  ProductPdpPageQueryKeys,
} from '@enums/react-query-keys';
import { ImagesService } from '@services/image-service';
import Environment from '@config/environment';
import imageQuality from '@config/image';
import useInitialRequestUrlQuery from '@hooks/use-initial-request-url-query';
import { usePreferences } from '@context/preferences.context';
import { languageToLocaleMap } from '@config/localization';
import { getAppInitialProps } from '@helpers/initial-props';
import styles from './index.module.scss';

const ProductCategoriesSuggestions = dynamic(
  () => import('@components/molecules/products/product-categories-suggestions/product-categories-suggestions'),
  { ssr: false },
);

const ProductDiscussionWrapper = dynamic(
  () => import('@components/molecules/products/product-discussion/product-discussion-wrapper'),
  { ssr: false },
);

const PdpRecommendationModules = dynamic(
  () => import('@components/product/pdp-recommendation-modules/pdp-recommendation-modules'),
  {
    ssr: false,
  },
);

const RecentlyViewedProductsWrapper = dynamic(
  () => import('@components/product/recently-viewed-products/recently-viewed-products-wrapper'),
  { ssr: false },
);

const HeroPDP = dynamic(() => import('@components/hero-pdp/hero-pdp'));

const PdpTop = dynamic(() => import('@components/product/pdp-top/pdp-top'));

type ProductPageProps = {
  breadcrumbs: Breadcrumb[];
  dehydratedState: DehydratedState;
  userAgent?: string;
};

const ProductPage: NextPageWithLayout<ProductPageProps> = (props): React.JSX.Element => {
  const { breadcrumbs, dehydratedState, userAgent } = props;
  useHydrate(dehydratedState);

  const { user } = useUser();
  const { product } = useProductQuery();
  const { addRecentlyViewedProduct } = useRecentlyViewedProducts();
  const { isRecirculation } = usePdpLayout();

  const { isFeatureEnabled } = useBucket();
  const dsaFeature = isFeatureEnabled((f) => f.reportAnItemOrUser, true) || false;

  const dataLayer = {
    env_template: 'product',
    screen_name: '/product',
    screen_category: 'product',
    screen_subcategory: 'product',
    ...mapProduct(product),
    ...mapProductPageUrl(product),
  };

  useEffect(() => {
    addRecentlyViewedProduct(product);
  }, [product]);

  const productImage = ImagesService.improveSEOProductPathImg(product?.pictures?.[0].path, product?.path);
  const { initialRequestUrl } = useInitialRequestUrlQuery();
  const { locale } = usePreferences();

  return (
    <>
      <UpdateDataLayerComponent
        data={dataLayer}
        userAgent={userAgent}
      />
      <Head
        metadata={product?.metadata}
        alternateVersions={product?.alternateVersions ?? []}
      >
        <meta
          key="og:image"
          property="og:image"
          content={`${Environment.imagesBaseUrl}${ImagesService.getResizingPrefix(
            productImage,
          )}/w=500,q=${imageQuality},f=auto,${productImage}`}
        />
        <meta
          key="og:url"
          property="og:url"
          content={initialRequestUrl.href.replace('.origin-fenx', '').replace('.fenx', '')}
        />
        <meta
          key="og:locale"
          property="og:locale"
          content={locale}
        />
        {Array.from(languageToLocaleMap).map(([, value]) => (
          <meta
            key={value}
            property="og:locale:alternate"
            content={value}
          />
        ))}
        <meta
          key="og:site_name"
          property="og:site_name"
          content="Vestiaire Collective"
        />
      </Head>
      <main className={styles.productPage}>
        <ProductSchema
          product={product}
          seoMetaData={product?.metadata}
        />
        <div
          className={clsx(
            styles.productPage__top,
            isRecirculation && styles['productPage__top--downsized'],
            'innerContainer',
          )}
        >
          {breadcrumbs && (
            <Breadcrumbs
              breadcrumbs={breadcrumbs}
              isCatalogPage={false}
              customClass={clsx(styles.productPage__top__breadcrumb, 'd-none d-md-flex')}
            />
          )}
          {isRecirculation ? <HeroPDP includeProductSchema={false} /> : <PdpTop />}
        </div>
        {isRecirculation && <PdpRecommendationModules />}
        <section className={styles.productPage__moreDetails}>
          <ProductDescription canReportProducts={user?.productReporter} />
        </section>
        {!isRecirculation && <PdpRecommendationModules />}
        <RecentlyViewedProductsWrapper />
        <ProductDiscussionWrapper />
        <section className={styles.productPage__categories}>
          <ProductCategoriesSuggestions product={product} />
          {user && dsaFeature && (
            <ProductReport
              productId={product.id}
              email={user.email}
              local={user.language.code}
            />
          )}
        </section>
      </main>
    </>
  );
};

ProductPage.Layout = [DefaultLayout];
ProductPage.PageType = 'ProductPage';

export const getServerSideProps: GetServerSideProps = async (context) => {
  const productSlugs = context?.params?.productSlug as string[];
  const fullSlug = `/${productSlugs.join('/')}`;

  const { serverAnonymousId } = getOrCreateLDCookiesSsr(context);

  const queryClient = new QueryClient();
  const initialProps = await getAppInitialProps(context, queryClient);

  // Extract id from slug
  let id: string | undefined;
  try {
    [id] = fullSlug.match(/\d+\.shtml$/);
    [id] = id.split('.shtml');
  } catch (err) {
    return {
      notFound: true,
    };
  }

  /*
    remove _next/data request prefix
    When do clientside navigation, if page fails, we will make sure it redirects to the correct path
  */
  const { req, query: contextQuery } = context;
  req.url = extractUrlfromPrerenderDataReq(contextQuery, 'productSlug');

  const {
    locale,
    preferences: { language },
    serverAxiosRequestData,
  } = extractFromContext(context);

  try {
    const mandatoryRequests = [
      serverSideTranslations(locale, ['default-layout', 'common']),
      ProductService.getProduct(id, serverAxiosRequestData),
      ProductService.getProductShippingInformation(id, serverAxiosRequestData),
    ];

    // These requests only get triggered based on certain conditions and may fail
    const optionalRequests = [
      getBreadcrumbs(fullSlug, serverAxiosRequestData),
      queryClient.fetchQuery<WelcomeVoucher>({
        queryKey: [GlobalQueryKeys.WELCOME_VOUCHER_ANONYMOUS_USER, 'PDP', id],
        queryFn: () => getAnonymousUserWelcomeVoucher('PDP', serverAnonymousId, serverAxiosRequestData, id),
      }),
    ];

    const promiseResults = await Promise.allSettled([...mandatoryRequests, ...optionalRequests]);

    const [translations, product, shipping, breadcrumbs] = promiseResults.map((promiseResult, index) => {
      if (promiseResult.status === 'fulfilled') {
        return promiseResult.value;
      }
      // Throw on error for tracking to pick it up for required requests, the rest can fail
      if (index < mandatoryRequests.length) {
        throw new RequestError(promiseResult.reason as string);
      }
      return null;
    }) as [SSRConfig, Product, Shipping, Breadcrumb[]];

    const path = context.req.url;

    // Get the URL for the product that correlates to the current language
    const alternateProductVersion: ContentsLocalizedPath = product?.alternateVersions?.find(
      (version: ContentsLocalizedPath) => {
        return version.language === language;
      },
    );

    // We must redirect to an alternate version if an alternate version exists
    const shouldRedirectToAnAlternateProductPath: boolean =
      !!alternateProductVersion && !path.includes(alternateProductVersion?.path);
    const isFromHeroPDP = !!context.req.headers['x-rewrite-hero-pdp'];

    // Redirect to the alternate version of the product if it exists
    // do not redirect if it's a hero pdp request
    if (shouldRedirectToAnAlternateProductPath && !isFromHeroPDP) {
      const { resolvedUrl } = context;
      const [, queryParams] = resolvedUrl.split('?');
      const hasQueryParams = !!queryParams;

      return {
        redirect: {
          destination: `${alternateProductVersion.path}${hasQueryParams ? `?${queryParams}` : ''}`,
          permanent: true,
        },
      };
    }

    if (product?.alternateVersions) {
      // map the alternate versions to be absolute urls
      product.alternateVersions = product.alternateVersions.map((version: ContentsLocalizedPath) => {
        return {
          ...version,
          path: generateAlternateLink(version),
        };
      });
    }
    // TODO: Refactor this once pdp has been refactored to work with hooks instead of context
    // Endgoal is to use queryClient to do the fetching, but until then, we're filling query client manually with the data
    // fetched above. We do this, so we can move some queries to react-query, while keeping backwards compatibility.
    queryClient.setQueryData([BreadcrumbsQueryKeys.BREADCRUMBS], breadcrumbs);
    queryClient.setQueryData([ProductPageQueryKeys.PRODUCT_SHIPPING], shipping);
    queryClient.setQueryData([ProductPdpPageQueryKeys.GET_PDP_PRODUCT], product);

    return {
      props: {
        ...initialProps,
        ...translations,
        dehydratedState: dehydrate(queryClient),
        product,
        breadcrumbs,
        userAgent: req?.headers?.['user-agent'],
      },
    };
  } catch (e) {
    // Redirect to parent page if a request failed
    if (isRequestError(e)) {
      if (!isRequest404Error(e)) {
        logger.error(
          e,
          'There was an api request error on the Product Page - getServerSideProps - [[...productSlug]].page.tsx',
        );
      }
      const { req } = context;
      // Removes trailing slash
      req.url = req.url.endsWith('/') ? req.url.slice(0, -1) : req.url;

      const pathRedirect: string[] = req.url.split('/');
      pathRedirect.pop();

      return {
        redirect: {
          destination: pathRedirect.join('/') + '/',
          permanent: true,
          basePath: false,
        },
      };
    }
    logger.error(e, 'The server failed to render the Product Page - getServerSideProps - [[...productSlug]].page.tsx');
    // Other errors will get logged
    throw e;
  }
};

export default ProductPage;
